// DeepSeek API service for AI-powered flash card generation
const DEEPSEEK_API_KEY = 'sk-04a8bc1279a0475eaab02f745561042b';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/chat/completions';

class DeepSeekApiService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  async generateFlashCards(topic, numberOfCards, language = 'English', difficulty = 'intermediate', essayContent = null) {
    try {
      const prompt = this.createFlashCardPrompt(topic, numberOfCards, language, difficulty, essayContent);
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'You are an educational content creator specializing in creating engaging flash cards for learning. Always respond with valid JSON format.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 2000,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek API Error Response:', errorText);
        throw new Error(`DeepSeek API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek API Response:', data);
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw content from API:', content);
      
      // Parse the JSON response
      let flashCardsData;
      try {
        flashCardsData = JSON.parse(content);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Content that failed to parse:', content);
        throw new Error('Failed to parse API response as JSON');
      }
      
      // Enhance with Unsplash images
      const enhancedCards = await this.enhanceWithImages(flashCardsData.cards);
      
      return {
        ...flashCardsData,
        cards: enhancedCards
      };
    } catch (error) {
      console.error('Error generating flash cards:', error);
      throw error;
    }
  }

  createFlashCardPrompt(topic, numberOfCards, language, difficulty, essayContent = null) {
    const basePrompt = essayContent 
      ? `Create ${numberOfCards} educational flash cards based on the following essay content about "${topic}" in ${language} language with ${difficulty} difficulty level.

ESSAY CONTENT:
${essayContent}

Generate questions that help students:
1. Understand the main themes and arguments
2. Learn key vocabulary and expressions used
3. Analyze the writing structure and techniques
4. Apply concepts to real-world situations
5. Improve their own writing skills

Focus on practical learning that helps students master both the content and writing techniques.`
      : `Create ${numberOfCards} educational flash cards about "${topic}" in ${language} language with ${difficulty} difficulty level.`;

    return `${basePrompt}

Please respond with a JSON object in this exact format:
{
  "title": "${topic}",
  "language": "${language}",
  "difficulty": "${difficulty}",
  "totalCards": ${numberOfCards},
  "cards": [
    {
      "id": 1,
      "question": "Question text here",
      "answer": "Answer text here",
      "explanation": "Brief explanation of the answer",
      "category": "Category name",
      "keywords": ["keyword1", "keyword2"],
      "imageQuery": "search term for related image"
    }
  ]
}

Guidelines:
- Make questions clear and educational
- Provide concise but informative answers
- Include brief explanations for better understanding
- Add relevant keywords for each card
- Suggest image search terms that would help visualize the concept
- Ensure content is appropriate for the specified difficulty level
- Questions should be diverse and cover different aspects of the topic`;
  }

  async enhanceWithImages(cards) {
    const enhancedCards = await Promise.all(
      cards.map(async (card, index) => {
        try {
          const imageUrl = await this.getUnsplashImage(card.imageQuery || card.category);
          return {
            ...card,
            imageUrl,
            imageAlt: `Illustration for ${card.question}`
          };
        } catch (error) {
          console.warn(`Failed to get image for card ${index + 1}:`, error);
          return {
            ...card,
            imageUrl: `https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80`,
            imageAlt: 'Educational content'
          };
        }
      })
    );
    
    return enhancedCards;
  }

  async getUnsplashImage(query) {
    // Use Unsplash Source API for random images based on query
    const searchQuery = encodeURIComponent(query);
    return `https://source.unsplash.com/800x600/?${searchQuery}&education`;
  }

  // Text-to-speech functionality
  speakText(text, language = 'en-US') {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = this.getLanguageCode(language);
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = 0.8;
      
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn('Text-to-speech not supported in this browser');
    }
  }

  getLanguageCode(language) {
    const languageMap = {
      'English': 'en-US',
      'Traditional Chinese': 'zh-HK', // Cantonese
      'Simplified Chinese': 'zh-CN', // Mandarin
      'Spanish': 'es-ES',
      'French': 'fr-FR',
      'German': 'de-DE',
      'Japanese': 'ja-JP',
      'Korean': 'ko-KR'
    };
    
    return languageMap[language] || 'en-US';
  }

  // Enhanced speech function with better language support
  speakTextEnhanced(text, language = 'en-US') {
    if ('speechSynthesis' in window) {
      // Stop any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      const langCode = this.getLanguageCode(language);
      
      utterance.lang = langCode;
      utterance.rate = 0.8;
      utterance.pitch = 1;
      utterance.volume = 0.8;
      
      // Try to find the best voice for the language
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.startsWith(langCode.split('-')[0]) && 
        (langCode === 'zh-HK' ? voice.name.includes('Cantonese') || voice.name.includes('Hong Kong') :
         langCode === 'zh-CN' ? voice.name.includes('Mandarin') || voice.name.includes('Chinese') :
         true)
      );
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn('Text-to-speech not supported in this browser');
    }
  }

  stopSpeaking() {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }
}

const deepSeekApiService = new DeepSeekApiService();
export default deepSeekApiService; 